import React, { ComponentProps } from "react";
import {
  Focusable,
  OverlayArrow,
  Tooltip as RACTooltip,
  TooltipTrigger,
  Button,
} from "react-aria-components";
import clsx from "clsx";

import styles from "./tooltip.module.css";

interface TooltipProps {
  className?: string;
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: ComponentProps<typeof RACTooltip>["placement"];
  display?: {
    overflow: "clip" | "ellipsis";
  };
  delay?: number;
  closeDelay?: number;
  whiteSpace?: "normal" | "nowrap";
  /**
   * The element the tooltip hangs off. Defaults to a button, which is the right
   * thing for plain text or an icon. Use "span" when the children already
   * contain interactive content (a button, a link) - a <button> inside a
   * <button> is invalid HTML and breaks hydration.
   */
  trigger?: "button" | "span";
}

export default function Tooltip({
  children,
  content,
  display,
  placement,
  className,
  delay = 250,
  closeDelay = 0,
  whiteSpace = "normal",
  trigger = "button",
}: TooltipProps) {
  const triggerClassName = clsx(styles.button, className, {
    [styles.ellipsis]: display?.overflow === "ellipsis",
  });

  return (
    <TooltipTrigger delay={delay} closeDelay={closeDelay}>
      {trigger === "span" ? (
        <Focusable>
          <span className={triggerClassName} tabIndex={0}>
            {children}
          </span>
        </Focusable>
      ) : (
        <Button className={triggerClassName}>{children}</Button>
      )}
      <RACTooltip
        placement={placement}
        className={clsx(styles.tooltip, {
          [styles.tooltipNoWrap]: whiteSpace === "nowrap",
        })}
      >
        <OverlayArrow className={styles.overlayArrow}>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <path d="M0 0 L4 4 L8 0" />
          </svg>
        </OverlayArrow>
        {content}
      </RACTooltip>
    </TooltipTrigger>
  );
}
